import type { NavSection } from '@wehunt/ui'
import { useTranslation } from 'react-i18next'
import { hasRole, hasRoleInDistrict, type CurrentUserDto } from '../../shared/auth/useCurrentUser'
import { canSee } from './roleVisibility'

// Routes mirror the legacy app exactly so existing bookmarks port over.
// Legacy uses hash routing (`#/news`); the new app uses regular routing (`/news`).
export function useViltrapportNavSections(
  user: CurrentUserDto | undefined,
  activeDistrictId: string | null,
): NavSection[] {
  const { t } = useTranslation('common')
  if (!user) return []
  const isAdmin = hasRoleInDistrict(user, activeDistrictId, 'Admin')

  const sections: NavSection[] = [
    {
      items: [
        { id: 'start',      label: t('nav.start'),      icon: 'dashboard', href: '/' },
        { id: 'lovligt',    label: t('nav.lovligt'),    icon: 'shield',    href: '/quotas' },
        {
          id: 'my-team',
          label: t('nav.myTeam'),
          icon: 'group',
          children: [
            { id: 'team-overview',     label: t('nav.teamOverview'),     icon: 'dashboard', href: '/my-team/overview' },
            { id: 'team-members',      label: t('nav.teamMembers'),      icon: 'user',     href: '/my-team/members' },
            { id: 'team-handout',      label: t('nav.teamHandout'),      icon: 'document', href: '/my-team/handout' },
            { id: 'team-invoices',     label: t('nav.teamInvoices'),     icon: 'cart',     href: '/my-team/invoices' },
            { id: 'team-reports',      label: t('nav.teamReports'),      icon: 'document', href: '/my-team/reports' },
            { id: 'team-other-reports', label: t('nav.teamOtherReports'), icon: 'document', href: '/my-team/otherReports' },
            { id: 'team-observations', label: t('nav.teamObservations'), icon: 'eye',      href: '/my-team/observations' },
            { id: 'team-sightings', label: t('nav.teamSightings'), icon: 'eye', href: '/my-team/sightings' },
            { id: 'team-official-reports', label: t('nav.teamOfficialReports'), icon: 'document', href: '/my-team/official-reports' },
            { id: 'team-final-report', label: t('nav.teamFinalReport'), icon: 'document', href: '/my-team/finalReport' },
          ],
        },
        { id: 'news',       label: t('nav.news'),       icon: 'bell',     href: '/news' },
        { id: 'statistics', label: t('nav.statistics'), icon: 'sparkle',  href: '/statistics/reports' },
        { id: 'documents',  label: t('nav.documents'),  icon: 'document', href: '/documents' },
        { id: 'calendar',   label: t('nav.calendar'),   icon: 'calendar', href: '/calendar' },
        { id: 'contacts',   label: t('nav.contacts'),   icon: 'user',     href: '/contacts' },
        { id: 'messages',   label: t('nav.messages'),   icon: 'chat',     href: '/messages' },
        { id: 'seasons',    label: t('nav.seasons'),    icon: 'leaf',     href: '/seasons' },
      ].filter((item) => canSee(user, item.id, activeDistrictId)),
    },
  ]

  if (isAdmin) {
    sections.push({
      label: t('nav.administration'),
      items: [
        { id: 'administration', label: t('nav.administration'), icon: 'settings', href: '/administration/overview' },
      ],
    })
  }

  const isSuperAdmin = hasRole(user, 'SuperAdmin')
  if (isSuperAdmin) {
    sections.push({
      label: t('nav.superAdmin'),
      items: [
        { id: 'impersonate', label: t('nav.impersonate'), icon: 'user', href: '#impersonate' },
      ],
    })
  }

  // Personal account — shown to everyone so a member can always reach their own
  // profile. The profile page is the only place to add/edit a phone number
  // after signing in, and it previously had no entry point in the shell.
  sections.push({
    label: t('nav.account'),
    items: [
      { id: 'profile', label: t('nav.profile'), icon: 'user', href: '/profile' },
    ],
  })

  // Contact Natlink support — shown to everyone. Uses a '#'-sentinel href like
  // '#impersonate'; AppShellLayout intercepts it and opens Natlink's contact
  // page (localized) in a new tab rather than routing to it.
  sections.push({
    label: t('nav.help'),
    items: [
      { id: 'contact-support', label: t('nav.contactSupport'), icon: 'chat', href: '#contact-support' },
    ],
  })

  return sections
}
